import React, { useState } from 'react'
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	TextField,
	Box,
	Typography,
	Tab,
	Tabs,
	CircularProgress,
	Alert,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import CodeIcon from '@mui/icons-material/Code'
import LinkIcon from '@mui/icons-material/Link'
import ImageIcon from '@mui/icons-material/Image'

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createQuoteNode } from '@lexical/rich-text'
import { $createCodeNode } from '@lexical/code'

import {
	$getSelection,
	$insertNodes,
	$isRangeSelection,
	FORMAT_TEXT_COMMAND,
	SELECTION_CHANGE_COMMAND,
	$createParagraphNode,
	$createTextNode,
} from 'lexical'
import { $setBlocksType } from '@lexical/selection'
import { $createHeadingNode } from '@lexical/rich-text'
import {
	INSERT_ORDERED_LIST_COMMAND,
	INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list'
import { apiEditorImageUpload } from 'api/api'

/**
 * Lexical 에디터용 툴바 플러그인 컴포넌트
 *
 * 제공하는 기능:
 * - 텍스트 포맷팅: 볼드, 이탤릭, 밑줄
 * - 헤딩: H1, H2
 * - 리스트: 순서 있는/없는 목록
 * - 블록 요소: 인용문, 코드 블록
 * - 링크 삽입: 마크다운 형식으로 링크 생성
 * - 이미지 삽입: URL 입력 또는 파일 업로드 지원
 *
 * Material-UI를 사용한 전문적인 다이얼로그 UI 제공
 */
function ToolbarPlugin() {
	const [editor] = useLexicalComposerContext()
	const [isBold, setIsBold] = useState(false)
	const [isItalic, setIsItalic] = useState(false)
	const [isUnderline, setIsUnderline] = useState(false)

	// 링크 다이얼로그 상태
	const [linkDialogOpen, setLinkDialogOpen] = useState(false)
	const [linkUrl, setLinkUrl] = useState('')
	const [linkText, setLinkText] = useState('')

	// 이미지 다이얼로그 상태
	const [imageDialogOpen, setImageDialogOpen] = useState(false)
	const [imageUrl, setImageUrl] = useState('')
	const [imageAlt, setImageAlt] = useState('')
	const [imageTabValue, setImageTabValue] = useState(0) // 0: URL, 1: 파일 업로드
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const [previewUrl, setPreviewUrl] = useState('')
	const [isUploading, setIsUploading] = useState(false)
	const [uploadError, setUploadError] = useState('')

	/**
	 * 에디터 선택 영역의 포맷팅 상태에 따라 툴바 버튼 활성화 상태 업데이트
	 * 선택된 텍스트가 볼드, 이탤릭, 밑줄 포맷을 가지고 있는지 확인하여 UI에 반영
	 */
	const updateToolbar = React.useCallback(() => {
		const selection = $getSelection()
		if ($isRangeSelection(selection)) {
			setIsBold(selection.hasFormat('bold'))
			setIsItalic(selection.hasFormat('italic'))
			setIsUnderline(selection.hasFormat('underline'))
		}
	}, [])

	/**
	 * 에디터 선택 변경 이벤트 리스너 등록
	 * 사용자가 텍스트를 선택하거나 커서를 이동할 때마다 툴바 상태 업데이트
	 */
	React.useEffect(() => {
		return editor.registerCommand(
			SELECTION_CHANGE_COMMAND,
			() => {
				updateToolbar()
				return false
			},
			1
		)
	}, [editor, updateToolbar])

	/**
	 * 링크 삽입 다이얼로그를 여는 함수
	 * 현재 선택된 텍스트가 있으면 링크 텍스트로 자동 설정
	 */
	const openLinkDialog = () => {
		editor.getEditorState().read(() => {
			const selection = $getSelection()
			if ($isRangeSelection(selection)) {
				const selectedText = selection.getTextContent()
				setLinkText(selectedText || '')
				setLinkUrl('')
				setLinkDialogOpen(true)
			}
		})
	}

	/**
	 * 링크를 마크다운 형식으로 에디터에 삽입하는 함수
	 * 형식: [링크텍스트](URL)
	 * 링크 텍스트가 없으면 URL을 텍스트로 사용
	 */
	const insertLink = () => {
		if (linkUrl.trim()) {
			editor.update(() => {
				const selection = $getSelection()
				if ($isRangeSelection(selection)) {
					const displayText = linkText.trim() || linkUrl
					selection.insertRawText(`[${displayText}](${linkUrl})`)
				}
			})
		}
		setLinkDialogOpen(false)
		setLinkUrl('')
		setLinkText('')
	}

	/**
	 * 파일 선택 시 이미지 파일 유효성 검사 및 미리보기 생성
	 * 이미지 파일만 허용하고 선택된 파일의 미리보기를 생성
	 * @param event - 파일 입력 이벤트
	 */
	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file && file.type.startsWith('image/')) {
			setSelectedFile(file)
			setUploadError('') // 새 파일 선택 시 에러 메시지 초기화
			const reader = new FileReader()
			reader.onload = (e) => {
				const result = e.target?.result as string
				setPreviewUrl(result)
			}
			reader.readAsDataURL(file)
		} else if (file) {
			setUploadError('이미지 파일만 업로드할 수 있습니다.')
		}
	}

	/**
	 * 이미지 삽입 다이얼로그를 여는 함수
	 * 모든 이미지 관련 상태를 초기화하고 다이얼로그를 표시
	 */
	const openImageDialog = () => {
		setImageUrl('')
		setImageAlt('이미지')
		setImageTabValue(0)
		setSelectedFile(null)
		setPreviewUrl('')
		setUploadError('')
		setImageDialogOpen(true)
	}

	/**
	 * 이미지를 에디터에 삽입하는 비동기 함수
	 * 두 가지 방식 지원:
	 * 1. URL 직접 입력 방식
	 * 2. 로컬 파일 업로드 후 서버에서 제공하는 URL 사용
	 *
	 * 이미지는 HTML <img> 태그 형태로 텍스트에 삽입되며,
	 * 최종 HTML 출력 시 실제 이미지로 렌더링됨
	 */
	const insertImage = async () => {
		let finalImageUrl = ''

		if (imageTabValue === 0 && imageUrl.trim()) {
			// URL 탭에서 이미지 URL 직접 입력
			finalImageUrl = imageUrl
		} else if (imageTabValue === 1 && selectedFile) {
			// 파일 업로드 탭에서 로컬 파일 선택
			setIsUploading(true)
			setUploadError('')
			try {
				const uploadResult = await apiEditorImageUpload(selectedFile)
				console.log('uploadResult:', uploadResult)
				if (uploadResult) {
					finalImageUrl = `${process.env.REACT_APP_HOST}/common/file/editorImageView${uploadResult}`
				} else {
					throw new Error('업로드 결과가 올바르지 않습니다.')
				}
			} catch (error) {
				console.error('파일 업로드 실패:', error)
				setUploadError('파일 업로드에 실패했습니다. 다시 시도해주세요.')
				setIsUploading(false)
				return // 에러가 발생하면 여기서 종료
			} finally {
				setIsUploading(false)
			}
		}

		if (finalImageUrl) {
			// insertNodes를 사용해서 실제 DOM 노드로 이미지 삽입
			editor.update(() => {
				const selection = $getSelection()
				if ($isRangeSelection(selection)) {
					// 텍스트 노드를 생성해서 이미지 마커 삽입
					const imageMarker = `[IMAGE:${finalImageUrl}:${imageAlt || '이미지'}]`
					const textNode = $createTextNode(imageMarker)

					console.log('Created text node with marker:', imageMarker)

					// 텍스트 노드를 에디터에 삽입
					$insertNodes([textNode])

					// 이미지 뒤에 빈 줄 추가
					const paragraphNode = $createParagraphNode()
					$insertNodes([paragraphNode])
				}
			})
		}

		// 다이얼로그 닫기 및 상태 초기화
		setImageDialogOpen(false)
		setImageUrl('')
		setImageAlt('')
		setSelectedFile(null)
		setPreviewUrl('')
	}

	return (
		<>
			<div
				style={{
					display: 'flex',
					gap: '4px',
					padding: '8px 12px',
					borderBottom: '1px solid #e1e4e8',
					backgroundColor: '#f6f8fa',
					flexWrap: 'wrap',
				}}
			>
				{/* 텍스트 포맷팅 */}
				<IconButton
					size="small"
					color={isBold ? 'primary' : 'default'}
					onClick={() => {
						editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
					}}
					title="굵게 (Ctrl+B)"
				>
					<FormatBoldIcon fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					color={isItalic ? 'primary' : 'default'}
					onClick={() => {
						editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
					}}
					title="기울임 (Ctrl+I)"
				>
					<FormatItalicIcon fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					color={isUnderline ? 'primary' : 'default'}
					onClick={() => {
						editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
					}}
					title="밑줄 (Ctrl+U)"
				>
					<FormatUnderlinedIcon fontSize="small" />
				</IconButton>

				<Divider
					orientation="vertical"
					flexItem
					sx={{ mx: 1 }}
				/>

				{/* 헤딩 */}
				<Button
					size="small"
					variant="outlined"
					onClick={() => {
						editor.update(() => {
							const selection = $getSelection()
							if ($isRangeSelection(selection)) {
								$setBlocksType(selection, () => $createHeadingNode('h1'))
							}
						})
					}}
					sx={{ minWidth: '40px', fontSize: '11px' }}
					title="큰 제목"
				>
					H1
				</Button>
				<Button
					size="small"
					variant="outlined"
					onClick={() => {
						editor.update(() => {
							const selection = $getSelection()
							if ($isRangeSelection(selection)) {
								$setBlocksType(selection, () => $createHeadingNode('h2'))
							}
						})
					}}
					sx={{ minWidth: '40px', fontSize: '11px' }}
					title="작은 제목"
				>
					H2
				</Button>

				<Divider
					orientation="vertical"
					flexItem
					sx={{ mx: 1 }}
				/>

				{/* 리스트 */}
				<IconButton
					size="small"
					onClick={() => {
						editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
					}}
					title="글머리 기호 목록"
				>
					<FormatListBulletedIcon fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					onClick={() => {
						editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
					}}
					title="번호 목록"
				>
					<FormatListNumberedIcon fontSize="small" />
				</IconButton>

				<Divider
					orientation="vertical"
					flexItem
					sx={{ mx: 1 }}
				/>

				{/* 기타 기능 */}
				<IconButton
					size="small"
					onClick={() => {
						editor.update(() => {
							const selection = $getSelection()
							if ($isRangeSelection(selection)) {
								$setBlocksType(selection, () => $createQuoteNode())
							}
						})
					}}
					title="인용문"
				>
					<FormatQuoteIcon fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					onClick={() => {
						editor.update(() => {
							const selection = $getSelection()
							if ($isRangeSelection(selection)) {
								const codeNode = $createCodeNode()
								selection.insertNodes([codeNode])
							}
						})
					}}
					title="코드 블록"
				>
					<CodeIcon fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					onClick={openLinkDialog}
					title="링크 삽입"
				>
					<LinkIcon fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					onClick={openImageDialog}
					title="이미지 삽입"
				>
					<ImageIcon fontSize="small" />
				</IconButton>
			</div>

			{/* 링크 삽입 다이얼로그 */}
			<Dialog
				open={linkDialogOpen}
				onClose={() => setLinkDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>링크 삽입</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="링크 텍스트"
						fullWidth
						variant="outlined"
						value={linkText}
						onChange={(e) => setLinkText(e.target.value)}
						sx={{ mb: 2 }}
						placeholder="화면에 표시될 텍스트"
					/>
					<TextField
						margin="dense"
						label="링크 URL"
						fullWidth
						variant="outlined"
						value={linkUrl}
						onChange={(e) => setLinkUrl(e.target.value)}
						placeholder="https://example.com"
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setLinkDialogOpen(false)}
						color="inherit"
					>
						취소
					</Button>
					<Button
						onClick={insertLink}
						variant="contained"
						disabled={!linkUrl.trim()}
					>
						삽입
					</Button>
				</DialogActions>
			</Dialog>

			{/* 이미지 삽입 다이얼로그 */}
			<Dialog
				open={imageDialogOpen}
				onClose={() => setImageDialogOpen(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>이미지 삽입</DialogTitle>
				<DialogContent>
					<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
						<Tabs
							value={imageTabValue}
							onChange={(event, newValue) => setImageTabValue(newValue)}
							aria-label="이미지 삽입 방법 선택"
						>
							<Tab label="URL 입력" />
							<Tab label="파일 업로드" />
						</Tabs>
					</Box>

					{/* URL 입력 탭 */}
					{imageTabValue === 0 && (
						<Box sx={{ mt: 2 }}>
							<TextField
								autoFocus
								margin="dense"
								label="이미지 URL"
								fullWidth
								variant="outlined"
								value={imageUrl}
								onChange={(e) => setImageUrl(e.target.value)}
								sx={{ mb: 2 }}
								placeholder="https://example.com/image.jpg"
							/>
						</Box>
					)}

					{/* 파일 업로드 탭 */}
					{imageTabValue === 1 && (
						<Box sx={{ mt: 2 }}>
							<Button
								component="label"
								variant="outlined"
								startIcon={<CloudUploadIcon />}
								sx={{ mb: 2, width: '100%', height: '56px' }}
							>
								이미지 파일 선택
								<input
									type="file"
									hidden
									accept="image/*"
									onChange={handleFileSelect}
								/>
							</Button>

							{selectedFile && (
								<Box sx={{ mt: 2 }}>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 1 }}
									>
										선택된 파일: {selectedFile.name}
									</Typography>
									{previewUrl && (
										<Box
											component="img"
											src={previewUrl}
											alt="미리보기"
											sx={{
												width: '100%',
												maxWidth: '300px',
												height: 'auto',
												border: '1px solid #e1e4e8',
												borderRadius: '4px',
											}}
										/>
									)}
								</Box>
							)}
						</Box>
					)}

					{/* 에러 메시지 표시 */}
					{uploadError && (
						<Alert
							severity="error"
							sx={{ mt: 2 }}
						>
							{uploadError}
						</Alert>
					)}

					{/* 공통 대체 텍스트 입력 */}
					<TextField
						margin="dense"
						label="대체 텍스트"
						fullWidth
						variant="outlined"
						value={imageAlt}
						onChange={(e) => setImageAlt(e.target.value)}
						placeholder="이미지 설명"
						sx={{ mt: 2 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setImageDialogOpen(false)}
						color="inherit"
						disabled={isUploading}
					>
						취소
					</Button>
					<Button
						onClick={insertImage}
						variant="contained"
						disabled={
							isUploading ||
							(imageTabValue === 0 && !imageUrl.trim()) ||
							(imageTabValue === 1 && !selectedFile)
						}
						startIcon={
							isUploading ? (
								<CircularProgress
									size={20}
									color="inherit"
								/>
							) : undefined
						}
					>
						{isUploading ? '업로드 중...' : '삽입'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default ToolbarPlugin
